<script setup>
import {
  IDHSReferral,
  titleI,
  titleII,
  titleIII,
  titleIV,
} from './../data/titleServiceOptions'

const router = useRouter()
const { locale, t } = useI18n()

// local state:
const partnerStaff = ref(null)
const partnerReferral = ref(null)
const contact = ref(null)

// Dyanmic form options / values that either come from API or modal form submission:
const partnerFundingCategory = ref([])
const partnerOrganizations = ref([])
const submittedContactInfo = ref(null)
const submittedPartnerStaffInfo = ref(null)
const veteranStatusOptions = ref([])
const accommodationOptions = ref([])

const titleIOptions = ref(titleI)
const titleIIOptions = ref(titleII)
const titleIIIOptions = ref(titleIII)
const titleIVOptions = ref(titleIV)
const IDHSReferralOptions = ref(IDHSReferral)

// Loading state:
const staffReferralModalSuccess = ref(false)
const contactModalSuccess = ref(false)
const formLoadingInitial = ref(true)
const formLoadingSubmission = ref(false)

// Conditional form input reveal states:
const titleICategorySelection = ref(false)
const titleIICategorySelection = ref(false)
const titleIIICategorySelection = ref(false)
const titleIVCategorySelection = ref(false)
const IDHSReferralCategorySelection = ref(false)

// Ref for tracking if we should create another referral
const createAnotherForCustomer = ref(false)

useHead({
  title: 'Referral Form | ScaleLIT',
  meta: [{
    name: 'description',
    content: 'This Referral Form is to be used by partners to gather the minimum customer information needed to make referrals to other organizations within the American Job Center (AJC) Network.',
  }],
})

watch(() => partnerReferral.value, () => {
  // Reveal additional program selection options if user chooses one of the following options:
  if (partnerReferral.value.referredPartnerFundingCategory) {
    const catRecordId = partnerReferral.value.referredPartnerFundingCategory
    const cat = partnerFundingCategory.value.find(item => item.value === catRecordId).label

    if (cat.includes('Career Services - Title IB')) {
      titleICategorySelection.value = true
      getPartnerOrganizations('Career Services - Title IB')
    }
    else {
      titleICategorySelection.value = false
    }

    if (cat.includes('Adult Basic Education and Literacy (ABE) - Title II')) {
      titleIICategorySelection.value = true
      getPartnerOrganizations('Adult Basic Education and Literacy (ABE) - Title II')
    }
    else {
      titleIICategorySelection.value = false
    }

    if (cat.includes('Wagner-Peyser Employment Services (ES) - Title III')) {
      titleIIICategorySelection.value = true
      getPartnerOrganizations('Wagner-Peyser Employment Services (ES) - Title III')
    }
    else {
      titleIIICategorySelection.value = false
    }

    if (cat.includes('Vocational Rehabilitation (VR) - Title IV')) {
      titleIVCategorySelection.value = true
      getPartnerOrganizations('Vocational Rehabilitation (VR) - Title IV')
    }
    else {
      titleIVCategorySelection.value = false
    }

    if (cat.includes('Supplemental Nutrition Assistance Program (SNAP) ') || cat.includes('Temporary Assistance for Needy Families (TANF)')) {
      IDHSReferralCategorySelection.value = true
      getPartnerOrganizations(cat) // Pass the exact category name
    }
    else {
      IDHSReferralCategorySelection.value = false
    }

    if (cat.includes('Senior Community Services Employment Program (SCSEP)'))
      getPartnerOrganizations('Senior Community Services Employment Program (SCSEP)')

    // If none of the above, get all receiving referral organizations
    if (!titleICategorySelection.value
        && !titleIICategorySelection.value
        && !titleIIICategorySelection.value
        && !titleIVCategorySelection.value
        && !IDHSReferralCategorySelection.value)
      getPartnerOrganizations(cat) // Pass the selected category
  }
  else {
    titleICategorySelection.value = false
    titleIICategorySelection.value = false
    titleIIICategorySelection.value = false
    titleIVCategorySelection.value = false
    IDHSReferralCategorySelection.value = false
    // Get all receiving referral organizations when no category is selected
    getPartnerOrganizations(null)
  }
})

// New function to reset form while preserving customer info
function resetFormPreserveCustomer() {
  const currentCustomer = partnerReferral.value.associatedCustomer
  const currentCustomerInfo = submittedContactInfo.value

  // Reset all form fields
  partnerReferral.value = {
    associatedCustomer: currentCustomer,
    referringStaff: partnerReferral.value.referringStaff, // Preserve staff member too
    referringServiceAreaFrom: partnerReferral.value.referringServiceAreaFrom, // Preserve service area
    createAnotherForCustomer: false,
  }

  // Reset conditional states
  titleICategorySelection.value = false
  titleIICategorySelection.value = false
  titleIIICategorySelection.value = false
  titleIVCategorySelection.value = false
  IDHSReferralCategorySelection.value = false

  // Keep customer info
  submittedContactInfo.value = currentCustomerInfo

  // Scroll to the funding category section
  const fundingCategoryElement = document.querySelector('[name="referredPartnerFundingCategory"]')
  if (fundingCategoryElement)
    fundingCategoryElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

async function getPartnerStaff({ search }) {
  /*
    Fetches all partner staff members from the API and maps them to a select input friendly format:
  */
  try {
    const partnerStaffResponse = await $fetch('/api/partner-staff', {
      method: 'GET',
    })
    const partnerOrgs = await getPartnerOrganizations(null)

    let partnerStaff = partnerStaffResponse.data.map((staff) => {
      let orgs = []
      if (staff['Employer (Affiliated Partner Organization)'] && Array.isArray(partnerOrgs)) {
        orgs = staff['Employer (Affiliated Partner Organization)']
          .map(orgId => partnerOrgs.find(org => org && org.value === orgId))
          .filter(Boolean)
      }

      let orgName = ''
      if (orgs.length > 0)
        orgName = orgs.map(org => org.label).join(', ')

      return {
        label: `${staff['First Name (Staff)']} ${staff['Last Name (Staff)']} - ${staff['Job Title']}`,
        value: staff.id,
        affiliation: orgName,
      }
    })

    if (search) {
      const lowercasedSearch = search.toLowerCase()
      partnerStaff = partnerStaff.filter(staff =>
        staff.label.toLowerCase().includes(lowercasedSearch),
      )
    }

    partnerStaff.sort((a, b) => a.label.localeCompare(b.label))
    return partnerStaff
  }
  catch (error) {
    console.error(error)
    return []
  }
}

async function getPartnerOrganizations(fundingCategory = null) {
  /*
    Fetches partner organizations from the API, filtered by funding category if specified.
    Maps them to a select input friendly format.
  */
  try {
    const endPointString = '/api/partner-organizations-by-funding'
    const params = { referrals: true }

    if (fundingCategory)
      params.fundingCategory = fundingCategory

    const partnerOrgResponse = await $fetch(endPointString, {
      method: 'GET',
      params,
    })

    partnerOrganizations.value = partnerOrgResponse.data.map(org => ({
      label: org.Name,
      value: org.id,
      description: org.Description,
    }))
    return partnerOrganizations.value
  }
  catch (error) {
    console.error(error)
  }
}

async function getServiceAreas({ search }) {
  /*
    Fetches all service areas from the API and maps them to a select input friendly format:
  */
  const selectedView = 'Partner Referral Form Areas'
  try {
    const serviceAreasResponse = await $fetch(`/api/service-areas?view=${selectedView}`, {
      method: 'GET',
    })

    let serviceAreas = serviceAreasResponse.data.map(area => ({ label: area.Name, value: area.id, notes: area.Notes }))

    if (search) {
      const lowercasedSearch = search.toLowerCase()
      serviceAreas = serviceAreas.filter(area =>
        area.label.toLowerCase().includes(lowercasedSearch),
      )
    }

    return serviceAreas
  }
  catch (error) {
    console.error(error)
    return []
  }
}

async function getCustomers({ search }) {
  /*
    Fetches customers from the API based on the search query or loads the first 20 rows if no search is provided.
    Maps the results to a select input friendly format.
  */
  try {
    const params = search ? { search } : { limit: 20 }
    const customerResponse = await $fetch('/api/customers', {
      method: 'GET',
      params,
    })

    if (!customerResponse.success) {
      console.error('Error fetching customers:', customerResponse.message)
      return []
    }

    const customers = customerResponse.data.map(cus => ({
      label: cus['Full Name'],
      value: cus.id,
      contact: `
        ${cus['Customer Email']} 
        <span class="inline-bullet">•</span> 
        ${cus['Customer Phone Number']} 
        <span class="inline-bullet">•</span> 
        ${cus['ZIP Code (Customer)']}
      `,
    }))

    customers.sort((a, b) => a.label.localeCompare(b.label))

    return customers
  }
  catch (error) {
    console.error('Error in getCustomers:', error)
    return []
  }
}

async function getPartnerFundingCategories({ search }) {
  /*
    Fetches all funding categories from the API and maps them to a select input friendly format:
  */
  try {
    const partnerFundingCategoryResponse = await $fetch('/api/partner-funding-categories?view=Partner Categories - Grid View', {
      method: 'GET',
    })

    partnerFundingCategory.value = partnerFundingCategoryResponse.data.map(partner => ({
      label: partner.Name,
      value: partner.id,
    }))

    let partnerFundingCategories = partnerFundingCategoryResponse.data.map(partner => ({
      label: partner.Name,
      value: partner.id,
    }))

    if (search) {
      const lowercasedSearch = search.toLowerCase()
      partnerFundingCategories = partnerFundingCategories.filter(partner =>
        partner.label.toLowerCase().includes(lowercasedSearch),
      )
    }

    return partnerFundingCategories
  }
  catch (error) {
    console.error(error)
    return []
  }
}

const formattedPartnerStaff = computed(() => {
  if (!partnerStaff.value)
    return null

  return {
    ...partnerStaff.value,
    partnerCategory: Array.isArray(partnerStaff.value.partnerCategory)
      ? partnerStaff.value.partnerCategory
      : [partnerStaff.value.partnerCategory],
    affiliatedServiceAreas: Array.isArray(partnerStaff.value.affiliatedServiceAreas)
      ? partnerStaff.value.affiliatedServiceAreas
      : [partnerStaff.value.affiliatedServiceAreas],
  }
})

function saveToLocalStorage() {
  if (partnerReferral.value.referringStaff)
    localStorage.setItem('scalelit-referringStaff', partnerReferral.value.referringStaff)

  if (partnerReferral.value.referringServiceAreaFrom)
    localStorage.setItem('scalelit-referringServiceAreaFrom', partnerReferral.value.referringServiceAreaFrom)
}

function loadFromLocalStorage() {
  const referringStaff = localStorage.getItem('scalelit-referringStaff')
  const referringServiceAreaFrom = localStorage.getItem('scalelit-referringServiceAreaFrom')

  if (referringStaff)
    partnerReferral.value.referringStaff = referringStaff

  if (referringServiceAreaFrom)
    partnerReferral.value.referringServiceAreaFrom = referringServiceAreaFrom
}

async function submitStaffReferralProfile() {
  formLoadingSubmission.value = true

  try {
    const submission = await $fetch('/api/partner-staff', {
      method: 'POST',
      body: formattedPartnerStaff.value,
    })
    staffReferralModalSuccess.value = true
    await getPartnerStaff({ search: null })
    partnerReferral.value.referringStaff = submission.data[0].id
    submittedPartnerStaffInfo.value = submission.data[0].fields['Full Name & Title & Partner Org (Staff)']
    formLoadingSubmission.value = false
  }
  catch (error) {
    console.error(error)
    formLoadingSubmission.value = false
  }
}

async function submitContactForm() {
  /*
    Pre-process any form data before submitted contact:
    --> Zipcode (incorrectly) needs to be a type Number because of the way the corresponding column in airtable is configured.
  */
  formLoadingSubmission.value = true
  const numberZip = Number(contact.value.zip)
  contact.value.zip = numberZip
  try {
    const submission = await $fetch('/api/customers', {
      method: 'POST',
      body: contact.value,
    })
    contactModalSuccess.value = true
    partnerReferral.value.associatedCustomer = submission.data[0].id
    submittedContactInfo.value = submission.data[0]['Full Name']
    formLoadingSubmission.value = false
  }
  catch (error) {
    console.error(error)
  }
}

async function submitPageForm() {
  formLoadingSubmission.value = true
  try {
    await $fetch('/api/partner-referrals', {
      method: 'POST',
      body: partnerReferral.value,
    })

    saveToLocalStorage()

    if (createAnotherForCustomer.value) {
      resetFormPreserveCustomer()
      formLoadingSubmission.value = false
    }
    else {
      // Original success redirect
      let successUrl = '/success?successType=partner-referral'
      if (locale.value !== 'en')
        successUrl = `/${locale.value}${successUrl}`
      router.push(successUrl)
    }
  }
  catch (error) {
    console.error(error)
    formLoadingSubmission.value = false
  }
}

async function getMultiSelectFieldOptions(tableName, fieldName, baseName) {
  const language = locale.value
  const endpoint = `/api/field-values?tableName=${tableName}&fieldName=${fieldName}&baseName=${baseName}${language ? `&languages=${language}` : ''}`

  try {
    const response = await $fetch(endpoint)
    // console.log(`API Response for ${fieldName}:`, response)

    if (response.success) {
      let options = []

      if (language
          && response.data.languageFields
          && response.data.languageFields[language]
          && response.data.languageFields[language].length > 0) {
        // console.log(`Language-specific options found for ${language} in ${fieldName}`)
        const langField = response.data.languageFields[language][0]
        const englishOptions = response.data.fieldOptions.options
        options = langField.options.map((option, index) => ({
          label: option,
          value: englishOptions[index] || option,
        }))
      }
      else {
        // console.log(`Using default options for ${fieldName}`)
        options = response.data.fieldOptions.options.map(option => ({
          label: option,
          value: option,
        }))
      }

      // console.log(`Processed options for ${fieldName}:`, options)
      return options
    }
    else {
      console.error(`Error fetching field options for ${fieldName}:`, response.message)
      return []
    }
  }
  catch (error) {
    console.error(`Error fetching field options for ${fieldName}:`, error)
    return []
  }
}

onMounted(async () => {
  veteranStatusOptions.value = await getMultiSelectFieldOptions('Customers', 'Veteran?')
  accommodationOptions.value = await getMultiSelectFieldOptions('Customers', 'Needs Accomodations?')

  await getPartnerOrganizations('')
  loadFromLocalStorage()

  formLoadingInitial.value = false
})

const headerDescription = computed(() => `
  <p>${t('This Referral Form is used by partners to gather minimum customer information for referrals to other AJC Network organizations.')}</p>
  <p>${t('Partners receiving referrals will get an email notification upon form submission.')}</p>
  <p>${t('Please complete this form to the best of your ability. Email the OSO team at ')}
  <a href="mailto:oso@scalelit.org">oso@scalelit.org</a>
  ${t('with any questions.')}</p>
  <p>${t('Partners are expected to respond to NEW referrals within 2 business days.')}</p>
  <p>${t('NOTE: After submitting this form, you can complete an auto-filled form for the same Customer or start a new referral.')}</p>
`)
</script>

<template>
  <div class="scalelit-form-page">
    <LanguageSelector />
    <section class="scalelit-form-page__body">
      <FormHeader
        :header-title="$t('Referral Form')"
        :is-html="true"
        :header-description="headerDescription"
      />
      <div class="scalelit-form-page__body__form">
        <client-only>
          <FormKit type="form" :actions="false" @submit="submitPageForm">
            <FormKit v-model="partnerReferral" type="group">
              <div class="scalelit-field-block">
                <div class="scalelit-field-block__header">
                  <h2>{{ $t('1. Referral Form') }}</h2>
                </div>
                <FormKit
                  outer-class="scalelit-field related-to-beneath"
                  name="referringStaff"
                  type="autocomplete"
                  :label="$t('Who is filling out this referral form?')"
                  :help="$t('Please select YOUR name from the Partner Referral Profile list. If you don\'t see it, select \'+ Create Partner Referral Profile\' to set up a referral profile for yourself.')"
                  :placeholder="$t('Start typing to lookup..')"
                  :options="getPartnerStaff"
                  :empty-message="$t('No staff members found.')"
                  open-on-click
                  clear-search-on-open
                  validation="required"
                >
                  <template #option="{ option, classes }">
                    <div class="scalelit-autocomplete-option" :class="[classes.option]">
                      <span>
                        {{ option.label }}
                      </span>
                      <div class="scalelit-field__inline-notes">
                        {{ option.affiliation }}
                      </div>
                    </div>
                  </template>
                </FormKit>

                <div class="scalelit-field related-to-above">
                  <button v-if="!submittedPartnerStaffInfo" type="button" data-a11y-dialog-show="partner-referral-modal" class="add-contact-btn">
                    {{ $t('OR, Create NEW Partner Referral Profile') }}
                  </button>
                  <button v-else :disabled="true">
                    {{ submittedPartnerStaffInfo }}
                  </button>
                </div>

                <FormKit
                  outer-class="scalelit-field"
                  name="referringServiceAreaFrom"
                  type="autocomplete"
                  :label="$t('Which Center or Service Area are you sending this referral FROM?')"
                  :help="$t('\'Service area\' is generally an AJC or IDES region. If you are connected to several, indicate the Area for this individual referral.')"
                  :placeholder="$t('Start typing to lookup..')"
                  :options="getServiceAreas"
                  :empty-message="$t('No service areas found.')"
                  open-on-click
                  clear-search-on-open
                  validation="required"
                >
                  <template #option="{ option, classes }">
                    <div class="scalelit-autocomplete-option" :class="[classes.option]">
                      <span>
                        {{ option.label }}
                      </span>
                      <p class="scalelit-field__inline-notes">
                        {{ option.notes }}
                      </p>
                    </div>
                  </template>
                </FormKit>
              </div>

              <div class="scalelit-field-block">
                <div class="scalelit-field-block__header">
                  <h2>{{ $t('2. Referred Customer Information') }}</h2>
                </div>
                <FormKit
                  outer-class="scalelit-field related-to-beneath"
                  name="associatedCustomer"
                  type="autocomplete"
                  :label="$t('Who is the Customer?')"
                  :help="$t('Search the name of the customer you are referring. If you don\'t see them, select \'+ Create Customer Referral Profile\' to set up a referral profile for them.')"
                  :placeholder="$t('Start typing to lookup..')"
                  :options="getCustomers"
                  :empty-message="$t('No customers found.')"
                  open-on-click
                  clear-search-on-open
                  validation="required"
                >
                  <template #option="{ option, classes }">
                    <div class="scalelit-autocomplete-option" :class="[classes.option]">
                      <span>
                        {{ option.label }}
                      </span>
                      <div class="scalelit-field__inline-notes" v-html="option.contact" />
                    </div>
                  </template>
                </FormKit>

                <div class="scalelit-field related-to-above">
                  <button v-if="!submittedContactInfo" type="button" data-a11y-dialog-show="customer-contact-modal" class="add-contact-btn">
                    {{ $t('OR, Create a Customer Referral Profile') }}
                  </button>
                  <button v-else :disabled="true">
                    {{ submittedContactInfo }}
                  </button>
                </div>
              </div>

              <div class="scalelit-field-block">
                <div class="scalelit-field-block__header">
                  <h2>{{ $t('3. Referral To') }}</h2>
                  <p>{{ $t('NOTE: Submitting multiple referrals? After submitting this form you will be able to complete an auto-filled form for the same Customer or begin a new referral for a new Customer.') }}</p>
                  <div class="scalelit-field-block__header__resources">
                    <ul>
                      <li>
                        <a href="https://www.scalelit.org/jobseeker">{{ $t('Click here Zipcode Map') }}</a>
                      </li>
                      <li><a href="https://cdn.disco.co/media/AJC_Manual_Volume_TWO_2024_5a6edc58-fe2f-4aac-91d1-7946f4339478.pdf?_gl=1*1etpg1a*_ga*NzU2MTQ2MzkwLjE3MDQ3MzIwNTU.*_ga_5LE4MCN5XE*MTcxMjc3MjUxMS4zNS4xLjE3MTI3NzI1MjAuMC4wLjA.">{{ $t('Click here for Partner information in the AJC Manual 2.0') }}</a></li>
                    </ul>
                  </div>
                </div>
                <FormKit
                  :outer-class="{
                    'scalelit-field': true,
                    'related-to-beneath': (titleICategorySelection || titleIICategorySelection || titleIIICategorySelection || titleIVCategorySelection || IDHSReferralCategorySelection),
                  }"
                  name="referredPartnerFundingCategory"
                  type="autocomplete"
                  :label="$t('Partner Category: Where is this referral being sent TO?')"
                  :help="$t('For example: Title II - Adult Education.')"
                  :placeholder="$t('Start typing to lookup..')"
                  :options="getPartnerFundingCategories"
                  :empty-message="$t('No categories found.')"
                  open-on-click
                  clear-search-on-open
                  validation="required"
                >
                  <template #option="{ option, classes }">
                    <div class="scalelit-autocomplete-option" :class="[classes.option]">
                      <span>
                        {{ option.label }}
                      </span>
                    </div>
                  </template>
                </FormKit>

                <FormKit
                  v-if="titleICategorySelection"
                  outer-class="scalelit-field related-to-above"
                  type="checkbox"
                  :label="$t('Title I - Career Services: What service(s) is the customer being referred for?')"
                  :help="$t('Career services and support programs are for people who are unemployed or underemployed and provide support through coaching, training, and more. *Add Custom Link for orientation registration/schedule if available.*')"
                  name="titleIReferralInfo"
                  :options="titleIOptions"
                />

                <FormKit
                  v-if="titleIICategorySelection"
                  outer-class="scalelit-field related-to-above"
                  type="checkbox"
                  :label="$t('Title II - Adult Basic Education (ABE): What service(s) is the customer being referred for?')"
                  :help="$t('Classes and tutoring for adults who want to improve reading, writing, math or English language skills. ** Not all services are available at all locations **')"
                  name="titleIIReferralInfo"
                  :options="titleIIOptions"
                />

                <FormKit
                  v-if="titleIIICategorySelection"
                  outer-class="scalelit-field related-to-above"
                  type="checkbox"
                  :label="$t('Title III - Referral Information')"
                  :help="$t('Get assistance finding on the job training and employment. Appointments can be scheduled by calling the Scheduling Hotline at (217) 558-0401. Individuals can call the Call Center at (800) 244-5631 with any questions that do not require in-person support. For questions about an unemployment insurance (UI) claims, certification and payment, ILogin, appeals call (800) 244-563. For information about fraud identity theft visit IDES website: https://ides.illinois.gov/unemployment/fraud-id-theft.html.')"
                  name="titleIIIReferralInfo"
                  :options="titleIIIOptions"
                />

                <FormKit
                  v-if="titleIVCategorySelection"
                  outer-class="scalelit-field related-to-above"
                  type="checkbox"
                  :label="$t('Title IV - Referral Information')"
                  :help="$t('This office offers many supports for people with disabilities. Please encourage residents to visit DHS public page to learn more about DRS services and the application process. https://wr.dhs.illinois.gov/wrpublic/wr/dynamic/referral.jsf')"
                  name="titleIVReferralInfo"
                  :options="titleIVOptions"
                />

                <FormKit
                  v-if="IDHSReferralCategorySelection"
                  outer-class="scalelit-field related-to-above"
                  type="checkbox"
                  :label="$t('IDHS - Referral Information')"
                  name="IDHSReferralInfo"
                  :options="IDHSReferralOptions"
                />

                <FormKit
                  outer-class="scalelit-field"
                  name="referredPartnerOrganization"
                  type="autocomplete"
                  :label="$t('Partner Organization: Where is this referral being sent TO?')"
                  :help="$t('This is selected based on the partner category above. If you\'re not sure where a referral should go, please refer to a Career Pathways Navigator in the appropriate area, and make a note at the end of the referral')"
                  :placeholder="$t('Start typing to lookup..')"
                  :options="partnerOrganizations"
                  :empty-message="$t('No organizations found.')"
                  open-on-click
                  clear-search-on-open
                  validation="required"
                >
                  <template #option="{ option, classes }">
                    <div class="scalelit-autocomplete-option" :class="[classes.option]">
                      <span>
                        {{ option.label }}
                      </span>
                      <p class="scalelit-field__inline-notes">
                        {{ option.description }}
                      </p>
                    </div>
                  </template>
                </FormKit>

                <FormKit
                  outer-class="scalelit-field"
                  type="textarea"
                  name="referralNotes"
                  :label="$t('Any notes for this referral?')"
                  :help="$t('For example: For Title I include industry interest and/or Veterans Status. For Title II, include highest level of education received. Include age range for Youth Programs or SCSEP.')"
                  :placeholder="$t('Write any additional notes about your submission here...')"
                />

                <FormKit
                  outer-class="scalelit-field"
                  type="checkbox"
                  :label="$t('Priority Referral')"
                  :help="$t('Please check box if this a warm referral. i.e. The customer is with sending partner when referral is being made and/or is aware of referral and is ready for outreach from receiving partner for services described above.')"
                  name="priorityReferral"
                />
              </div>

              <div class="submit-section space-y-4">
                <FormKit type="submit" :label="$t('Submit')" :disabled="formLoadingSubmission" />
                <FormKit
                  v-model="createAnotherForCustomer"
                  type="checkbox"
                  :label="$t('Create another referral for this customer after submission')"
                  :help="$t('This will preserve the customer and staff information while clearing other fields')"
                />
              </div>
            </FormKit>
          </FormKit>
        </client-only>
      </div>
    </section>
    <!-- new modal: -->
    <AccessibleModal
      modal-id="partner-referral-modal"
      :modal-title="$t('Set up your Staff Referral profile')"
      :modal-description="$t('After submitting this form you will be able to continue the registration process.')"
      :submission-success="staffReferralModalSuccess"
    >
      <FormKit type="form" :actions="false" @submit="submitStaffReferralProfile">
        <FormKit v-model="partnerStaff" type="group">
          <FormKit
            outer-class="scalelit-field"
            type="text"
            name="firstName"
            :label="$t('Your First Name')"
            :placeholder="$t('Enter your first name..')"
            validation="required"
          />
          <FormKit
            outer-class="scalelit-field"
            type="text"
            name="lastName"
            :label="$t('Your Last Name')"
            :placeholder="$t('Enter your last name..')"
            validation="required"
          />
          <FormKit
            outer-class="scalelit-field"
            type="text"
            name="jobTitle"
            :label="$t('Your Job title')"
            :placeholder="$t('Enter your job title..')"
            validation="required"
          />
          <FormKit
            outer-class="scalelit-field"
            type="text"
            name="email"
            :label="$t('Your Email')"
            :placeholder="$t('Enter your email address..')"
            validation="required|email"
          />

          <FormKit
            outer-class="scalelit-field"
            name="partnerOrganization"
            type="autocomplete"
            :label="$t('What organization are you connected with?')"
            :help="$t('For example, E&ES, Easter Seals, or IDES.')"
            :placeholder="$t('Start typing to lookup..')"
            :options="partnerOrganizations"
            :empty-message="$t('No organizations found.')"
            open-on-click
            clear-search-on-open
            validation="required"
          >
            <template #option="{ option, classes }">
              <div class="scalelit-autocomplete-option" :class="[classes.option]">
                <span>
                  {{ option.label }}
                </span>
                <p class="scalelit-field__inline-notes">
                  {{ option.description }}
                </p>
              </div>
            </template>
          </FormKit>

          <FormKit
            outer-class="scalelit-field"
            name="partnerCategory"
            type="autocomplete"
            :label="$t('What Partner Category are you connected with?')"
            :help="$t('For example, Title I Career Services. Choose as many as apply.')"
            :placeholder="$t('Start typing to lookup..')"
            :options="getPartnerFundingCategories"
            :empty-message="$t('No categories found.')"
            multiple
            :close-on-select="false"
            open-on-click
            clear-search-on-open
            validation="required"
          >
            <template #option="{ option, classes }">
              <div class="scalelit-autocomplete-option" :class="[classes.option]">
                <span>
                  {{ option.label }}
                </span>
              </div>
            </template>
          </FormKit>

          <FormKit
            outer-class="scalelit-field"
            name="affiliatedServiceAreas"
            type="autocomplete"
            :label="$t('What AJC or Service Area are you connected with?')"
            :help="$t('For example, South Suburban AJC or IDES Metro South. Choose as many as apply.')"
            :placeholder="$t('Start typing to lookup..')"
            :options="getServiceAreas"
            :empty-message="$t('No categories found.')"
            multiple
            :close-on-select="false"
            open-on-click
            clear-search-on-open
            validation="required"
          >
            <template #option="{ option, classes }">
              <div class="scalelit-autocomplete-option" :class="[classes.option]">
                <span>
                  {{ option.label }}
                </span>
                <p class="scalelit-field__inline-notes">
                  {{ option.notes }}
                </p>
              </div>
            </template>
          </FormKit>

          <FormKit type="submit" :label="$t('Register')" />
        </FormKit>
      </FormKit>
    </AccessibleModal>
    <!-- new modal: -->
    <AccessibleModal
      modal-id="customer-contact-modal"
      :modal-title="$t('Add Customer Contact Information')"
      :modal-description="$t('After submitting this form you will be able to continue the registration process.')"
      :submission-success="contactModalSuccess"
    >
      <FormKit type="form" :actions="false" @submit="submitContactForm">
        <FormKit v-model="contact" type="group">
          <FormKit
            outer-class="scalelit-field"
            type="text"
            name="firstName"
            :label="$t('Customer First Name')"
            :placeholder="$t('Enter your first name..')"
            validation="required"
          />
          <FormKit
            outer-class="scalelit-field"
            type="text"
            name="lastName"
            :label="$t('Customer Last Name')"
            :placeholder="$t('Enter your last name..')"
            validation="required"
          />
          <FormKit
            outer-class="scalelit-field"
            type="text"
            name="email"
            :label="$t('Customer Email')"
            :placeholder="$t('Enter your email address..')"
            validation="required|email"
          />
          <FormKit
            outer-class="scalelit-field"
            type="tel"
            name="phone"
            :label="$t('Customer Phone Number')"
            placeholder="xxx-xxx-xxxx"
            validation="required | matches:/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/"
            :validation-messages="{
              matches: $t('Phone number must be in the format xxx-xxx-xxxx'),
            }"
            validation-visibility="dirty"
          />
          <FormKit
            outer-class="scalelit-field"
            type="text"
            name="zip"
            :label="$t('Customer ZIP code')"
            :placeholder="$t('Enter your customer ZIP code..')"
            validation="required|length:5"
          />

          <FormKit
            outer-class="scalelit-field"
            type="select"
            :label="$t('Veteran?')"
            name="veteranStatus"
            :placeholder="$t('Select veteran status..')"
            :options="veteranStatusOptions"
            validation="required"
          />

          <FormKit
            outer-class="scalelit-field"
            type="select"
            :label="$t('Needs accommodations?')"
            name="needsAccomodations"
            :placeholder="$t('Select accommodation status..')"
            :options="accommodationOptions"
            validation="required"
          />

          <FormKit
            outer-class="scalelit-field"
            type="textarea"
            name="referralNotes"
            :label="$t('Any notes for this Customer?')"
            :help="$t('Note specific to Customer i.e. No car, no personal phone number, requires accommodations etc. Do NOT list specific referral notes here.')"
            :placeholder="$t('Do NOT list specific referral notes here. Add referral notes i.e. WIOA manufacturing, job readiness, ESL etc. on next part of form.')"
          />

          <FormKit name="entryPoint" type="hidden" value="HUB Self-Entry" />

          <FormKit type="submit" :label="$t('Register')" />
        </FormKit>
      </FormKit>
    </AccessibleModal>

    <transition name="fade">
      <LoadingOverlay v-if="formLoadingSubmission" :loading-text="$t('Saving your info...')" />
    </transition>

    <transition name="fade">
      <LoadingOverlay v-if="formLoadingInitial" :loading-text="$t('Preparing form...')" />
    </transition>
  </div>
</template>

<style scoped lang="scss">
@import './assets/scss/breakpoints';

.submit-section {
  display: flex;
  align-items: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;

  @include breakpoint(mobile) {
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem;
  }
}

.scalelit-form-page [data-family="button"].formkit-outer {
  padding: 0;
  margin-right: 2rem;
  padding-right: 1rem;
  border-right: 1px solid #e5e7eb;

  @include breakpoint(mobile) {
    width: 100%;
    margin-right: 0;
    padding-right: 0;
    border-right: none;
  }
}

.submit-section__checkbox {
  margin-bottom: 1rem;
}

:deep(.submit-section__checkbox .formkit-label) {
  font-weight: 500;
}

:deep(.submit-section__checkbox .formkit-help) {
  color: #6b7280;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

:deep(.submit-section__button) {
  margin-top: 1rem;
}
</style>
